import { Injectable } from '@angular/core';
import { compactDecrypt, importPKCS8 } from 'jose';

import { environment } from '../../environments/environment';

const PUBLIC_KEY_BASE64 =
  'TUlJRHl6Q0NBck9nQXdJQkFnSUpBT0pUMXBqaGpiOUtNQTBHQ1NxR1NJYjNE' +
  'UUVCQ3dVQU1Id3hDekFKQmdOVkJBWVRBbEJJTVE0d0RBWURWUVFJREFWRWRX' +
  'MXRlVEVPTUF3R0ExVUVCd3dGUkhWdGJYa3hEakFNQmdOVkJBb01CVVIxYlcx' +
  'NU1RNHdEQVlEVlFRTERBVkVkVzF0ZVRFdE1Dc0dBMVVFQXd3a2FIUjBjSE02' +
  'THk5a1ozWTRiM1pyYjJFMGNIazRMbU5zYjNWa1puSnZiblF1Ym1WME1CNFhE' +
  'VEU1TURNeE1URXlOVFkxTlZvWERUSXhNVEl5T1RFeU5UWTFOVm93ZkRFTE1B' +
  'a0dBMVVFQmhNQ1VFZ3hEakFNQmdOVkJBZ01CVVIxYlcxNU1RNHdEQVlEVlFR' +
  'SERBVkVkVzF0ZVRFT01Bd0dBMVVFQ2d3RlJIVnRiWGt4RGpBTUJnTlZCQXNN' +
  'QlVSMWJXMTVNUzB3S3dZRFZRUUREQ1JvZEhSd2N6b3ZMMlJuZGpodmRtdHZZ' +
  'VFJ3ZVRndVkyeHZkV1JtY205dWRDNXVaWFF3Z2dFaU1BMEdDU3FHU0liM0RR' +
  'RUJBUVVBQTRJQkR3QXdnZ0VLQW9JQkFRRFJlMGhKT3F1aTIxRGw1QVc5c3ZJ' +
  'SkZrSHhCRlBnY0l3OVhBdzJUNVFMdzF6Qkh0cGRZNnJGY3cwdGd4NzFnZ1h6' +
  'c1NHSHRvQ3R6NEt1bVA2UG9JRlpQWFNyV3ltbzZWMU9xdHhsMUZQeUNIbkpL' +
  'Ym5NRXZjb3d2dGVLcis2Qk1HVldoTVZlcmYrOU1KbytIYlR5ZUlyYjNqK2pC' +
  'WVNrS3R2RTd6aVBDSHFJcmpmRXFvZ2ZwV0F6L2w5YUlDU2haVzdEYTlleVEz' +
  'TGlHM0djMGJjRmVGbzZ6U2xicUF0OG5LakFSd0taOFQxV3BDM3BHMmtXM2Z0' +
  'azF3M1dRY2o2S3FIZll6VWZKOWtmWGVvdkZhY0dYSFpjY25oZzVOR3BWRGNN' +
  'Y015TXNveE9JeUR4Vi82em1RcU1peWd0MEFmN09xN1VpZ0ZxOEQyRjBEamQz' +
  'L1hRTWNKQWdNQkFBR2pVREJPTUIwR0ExVWREZ1FXQkJTdjlsL1lwWU5zYVZt' +
  'ckg1ZHM3TEF6amJxMTd6QWZCZ05WSFNNRUdEQVdnQlN2OWwvWXBZTnNhVm1y' +
  'SDVkczdMQXpqYnExN3pBTUJnTlZIUk1FQlRBREFRSC9NQTBHQ1NxR1NJYjNE' +
  'UUVCQ3dVQUE0SUJBUUMzQXBobHM1RlJYY09BVUp6enZJdldUU21oKzN3MmJM' +
  'RXBlMDYzWUw4L3M2QXlwMUdRZTF3aC9HNVRhZG55VEw3L3l2OXIzSVJCTGJC' +
  'MjFFeVRBUDNYOHdHSllMU3YxU2h2Q1R2NU1IYXlnb0xvTXMwdXkxZHZSWjdj' +
  'TzF3MWdwR2tJUnBiU2xSelZIdERhc3hNRXJQTFhFZDFKK0E1RERlSm1WOThs' +
  'SkV1V2dzSkNWemludTBHeTZLK3U5anVJRFdKZmErM0ZpemN3QUpmWHdhalFU' +
  'bENXUHArOUtlUjVMNExHV3BSNGhrNk9XQlhsZHRqQlpRRkVwVnZSTzM0Q1Y3' +
  'TGQyM0pObC9jZmJYUllicjA0TGk1L0EyNzZtamoxbCtSZmt2YnhKeThKT3ls' +
  'NzF4czZiZnM1U0NkbENyTDV0NlFEbEduN296SWpQVXlDNExndFhWSw==';

@Injectable({ providedIn: 'root' })
export class JoseService {
  readonly publicKey = atob(PUBLIC_KEY_BASE64);
  readonly algorithm = 'RSA-OAEP';
  readonly encryption = 'A256GCM';
  private readonly privateKey = importPKCS8(
    environment.privateKey,
    this.algorithm,
  );

  async decrypt(payload: string): Promise<string> {
    const { plaintext } = await compactDecrypt(payload, await this.privateKey);
    return new TextDecoder().decode(plaintext);
  }
}